import React, { useState } from "react";
// import Logo from "../../assets/hotel_logo.png";
import { FaTimes, FaShareAlt, FaPrint } from "react-icons/fa";
import User from "../../models/User";
import { formatDate } from "../../utils/date"
import { sendRequest } from "../../utils/api";
import { useToast } from "../../context/ToastContext";
import FullPageSpinner from "../../components/FullPageSpinner";
import Logo from "../../assets/hotel_logo.png";
import { formatNaira } from "../../utils/bill";

interface PayslipProps {
    onClose: () => void;
    employee: User;
    company: { name: string, logo: string };
    createdAt?: string;
    earnings: PayRoll[];
    deductions: PayRoll[];
}


const Payslip: React.FC<PayslipProps> = ({ onClose, employee, company, earnings, createdAt, deductions }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState<boolean>(false);

    const totalEarnings = earnings.reduce((sum, e) => sum + parseFloat(e.amount), 0);
    const totalDeductions = deductions.reduce((sum, d) => sum + parseFloat(d.amount), 0);
    const netPay = totalEarnings - totalDeductions;

    const handlePrint = () => {
        window.print();
    };

    const employeeName = employee.firstName + " " + employee.lastName;

    const date = createdAt ? new Date(createdAt) : new Date();

    const handleShare = async () => {
        setLoading(true);
        try {
            await sendRequest("POST", "/users/share-payslip", { userIds: [employee.id] });
            showToast("Payslip shared successfully.", { type: "success" });
        } catch (err: any) {
            console.log("Error sharing payslip:", err);
            showToast(
                "Failed to share payslip. Please check your network and try again.",
                { type: "error" }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-start z-50 p-4 overflow-auto animate-fadeIn">
            <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full relative p-6 print:p-0 print:shadow-none print:rounded-none flex flex-col">
                {/* Top-right close */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-xl"
                >
                    {FaTimes({})}
                </button>

                <FullPageSpinner show={loading} />

                {/* Scrollable content */}
                <div className="overflow-y-auto max-h-[80vh]">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-6 border-b pb-4 print:border-b-2">
                        <img
                            src={Logo}
                            alt="Company Logo"
                            className="h-12 mb-2 sm:mb-0 print:h-16"
                        />
                        <div className="text-center sm:text-right">
                            <h1 className="text-2xl font-bold print:text-3xl">{company.name}</h1>
                            <p className="text-gray-500 print:text-black">
                                Payslip for:{" "}
                                <span className="font-semibold">
                                    {date.toLocaleString("default", { month: "long", year: "numeric" })}
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Employee Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm print:text-base">
                        <div>
                            <p>
                                <span className="font-semibold">Employee Name:</span> {employeeName}
                            </p>
                            <p>
                                <span className="font-semibold">Employee ID:</span> {employee.staffId}
                            </p>
                        </div>
                        <div>
                            <p>
                                {/* <span className="font-semibold">Department:</span> {employee.department} */}
                            </p>
                            <p>
                                <span className="font-semibold">Position:</span> {employee.portfolio}
                            </p>
                        </div>
                    </div>

                    {/* Earnings & Deductions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 print:gap-12">
                        <div>
                            <h2 className="font-bold text-gray-700 mb-2 print:text-black">Earnings</h2>
                            <table className="w-full text-left border border-gray-200 rounded-lg print:border-2">
                                <tbody>
                                    {earnings.map((e, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b border-gray-200 last:border-b-0 print:border-b"
                                        >
                                            <td className="px-3 py-2 capitalize">{e.title}</td>
                                            <td className="px-3 py-2 font-semibold">₦{formatNaira(parseInt(e.amount))}</td>
                                        </tr>
                                    ))}
                                    <tr className="font-bold bg-gray-100 print:bg-gray-200">
                                        <td className="px-3 py-2">Total Earnings</td>
                                        <td className="px-3 py-2">₦{formatNaira(totalEarnings)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <h2 className="font-bold text-gray-700 mb-2 print:text-black">Deductions</h2>
                            <table className="w-full text-left border border-gray-200 rounded-lg print:border-2">
                                <tbody>
                                    {deductions.map((d, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b border-gray-200 last:border-b-0 print:border-b"
                                        >
                                            <td className="px-3 py-2 capitalize">{d.title}</td>
                                            <td className="px-3 py-2 font-semibold">₦{formatNaira(parseInt(d.amount))}</td>
                                        </tr>
                                    ))}
                                    <tr className="font-bold bg-gray-100 print:bg-gray-200">
                                        <td className="px-3 py-2">Total Deductions</td>
                                        <td className="px-3 py-2">₦{formatNaira(totalDeductions)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Net Pay */}
                    <div className="bg-green-100 p-4 rounded-lg text-center mb-6 print:bg-green-200">
                        <h2 className="text-lg font-bold text-green-800 print:text-green-900">Net Pay</h2>
                        <p className="text-2xl font-extrabold text-green-900 print:text-3xl">
                            ₦{formatNaira(netPay)}
                        </p>
                    </div>

                    {/* Footer Info */}
                    <div className="flex flex-col sm:flex-row justify-between items-center text-gray-500 text-sm border-t pt-4 print:text-black print:border-t-2">
                        {(!employee.accountNo && !employee.bank) ? (
                            // Render first block if both are empty
                            <>
                                <p>Payment Date: {formatDate(date)}</p>
                                <p>Payment Method: Bank Transfer</p>
                            </>
                        ) : (
                            // Render second block if either has value
                            <>
                                <p>Payment Medium: <span className="capitalize">{employee.bank}</span></p>
                                <p>Acct No.: {employee.accountNo}</p>
                            </>
                        )}
                    </div>

                    {/* <div className="mt-6 text-right">
                        <p className="font-semibold">Authorized by: ___________________</p>
                    </div> */}
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end items-center space-x-3 text-sm mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 print:hidden rounded bg-gray-300 hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handlePrint}
                        className="px-4 py-2 print:hidden  rounded bg-blue-500 text-white hover:bg-blue-600 flex items-center space-x-1"
                    >
                        {FaPrint({})} <span>Print</span>
                    </button>
                    <button
                        onClick={handleShare}
                        className="px-4 py-2 print:hidden rounded bg-green-500 text-white hover:bg-green-600 flex items-center space-x-1"
                    >
                        {FaShareAlt({})} <span>Share</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Payslip;